import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import Head from 'next/head'
import { useRouter } from 'next/router'

import { SITE_URL, siteConfig } from '@/constants/site'

interface ShareImageOptions {
    title: string
    figure: string
    shape: string
}

/**
 * Absolute address of the generated 1200 by 630 share card for a page.
 */
export const shareImageUrl = ({ title, figure, shape }: ShareImageOptions): string => {
    const params = new URLSearchParams({ title, fig: figure, shape })

    return `${SITE_URL}/api/og?${params.toString()}`
}

const pagePath = (asPath: string): string => {
    const path = asPath.split('#')[0].split('?')[0] || '/'

    return path === '/' ? '' : path
}

interface SocialMetaProps {
    pageTitle: string
    description: string
    image: string
    url: string
}

/**
 * Open Graph and Twitter tags. Rendered inside a Head by the caller.
 */
export const socialMeta = ({ pageTitle, description, image, url }: SocialMetaProps): Array<ReactNode> => [
    <meta key='description' name='description' content={description} />,
    <link key='canonical' rel='canonical' href={url} />,
    <meta key='og:site_name' property='og:site_name' content={siteConfig.name} />,
    <meta key='og:title' property='og:title' content={pageTitle} />,
    <meta key='og:description' property='og:description' content={description} />,
    <meta key='og:type' property='og:type' content='website' />,
    <meta key='og:url' property='og:url' content={url} />,
    <meta key='og:image' property='og:image' content={image} />,
    <meta key='og:image:width' property='og:image:width' content='1200' />,
    <meta key='og:image:height' property='og:image:height' content='630' />,
    <meta key='twitter:card' name='twitter:card' content='summary_large_image' />,
    <meta key='twitter:title' name='twitter:title' content={pageTitle} />,
    <meta key='twitter:description' name='twitter:description' content={description} />,
    <meta key='twitter:image' name='twitter:image' content={image} />
]

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    figure,
    shape = 'library'
}): ReactNode => {
    const router = useRouter()
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const url = `${SITE_URL}${pagePath(router.asPath)}`
    const image = ogImage ?? shareImageUrl({
        title: title ?? 'Architecture and building intelligence',
        figure: figure ?? `Fig. 00 / ${title ?? 'Home'} / Stockholm, Lisbon, New York`,
        shape
    })

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            {socialMeta({ pageTitle, description, image, url })}
        </Head>
    )
}
